import React from 'react';
import './OptimizedImage.css';

interface OptimizedImageProps {
  /** Full image path (e.g., "/content/images/hero.png") */
  src: string;
  /** Alt text for accessibility */
  alt: string;
  /** Image width for layout stability */
  width: number;
  /** Image height for layout stability */
  height: number;
  /** Loading strategy */
  loading?: 'lazy' | 'eager';
  /** CSS classes */
  className?: string;
  /** Priority hint for LCP images */
  fetchpriority?: 'high' | 'low' | 'auto';
  /** Responsive sizes attribute for different viewports */
  sizes?: string;
}

/**
 * Lightweight image component using native <picture> element.
 *
 * Benefits:
 * - Zero JavaScript overhead (no format detection)
 * - Native browser optimization
 * - Automatic format selection by browser
 * - Layout stability with width/height attributes
 *
 * The browser automatically selects the best format it supports:
 * 1. AVIF (if supported) - smallest file size
 * 2. WebP (if supported) - good compression
 * 3. Optimized PNG/JPG (fallback) - universal support
 *
 * Usage:
 * <OptimizedImage
 *   src="/content/images/hero.png"
 *   alt="Hero image"
 *   width={1024}
 *   height={683}
 *   loading="eager"
 *   fetchpriority="high"
 * />
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  loading = 'lazy',
  className = '',
  fetchpriority = 'auto',
  sizes,
}) => {
  // Type guard to ensure src is a string
  if (typeof src !== 'string') {
    console.warn('OptimizedImage: src must be a string');
    return null;
  }

  // Inline styles for guaranteed space reservation
  const imgStyle: React.CSSProperties = {
    aspectRatio: `${width} / ${height}`,
    width: '100%',
    height: 'auto',
  };

  // Check if this is an external URL (Firebase, CDN, etc.)
  const isExternalUrl = src.startsWith('http://') || src.startsWith('https://');

  // Check if this is an article image with responsive variants
  const isArticleImage = src.includes('/content/images/') && !isExternalUrl;

  // Extract base path and extension for article images
  const match = src.match(/^(.+)\.(png|jpg|jpeg)$/i);

  // For external URLs or images without expected pattern, render simple img tag
  if (isExternalUrl || !match || !isArticleImage) {
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        className={`optimized-image ${className}`}
        style={imgStyle}
        // @ts-ignore - fetchpriority is not in React types yet
        fetchpriority={fetchpriority}
      />
    );
  }

  const basePath = match[1];
  const extension = match[2];

  // Generate source paths
  const fallbackSrc = `${basePath}-optimized.${extension}`;

  // Default responsive sizes if not provided
  const responsiveSizes = sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';

  // Generate srcset for different sizes
  // Note: This assumes your image optimization process creates these variants
  const generateSrcSet = (ext: string) => {
    return `${basePath}-400w.${ext} 400w, ${basePath}-800w.${ext} 800w, ${basePath}-1200w.${ext} 1200w`;
  };

  return (
    <picture>
      {/* AVIF - Best compression, newest format */}
      <source srcSet={generateSrcSet('avif')} sizes={responsiveSizes} type="image/avif" />

      {/* WebP - Good compression, wide support */}
      <source srcSet={generateSrcSet('webp')} sizes={responsiveSizes} type="image/webp" />

      {/* Optimized original format - Universal fallback */}
      <img
        src={fallbackSrc}
        srcSet={generateSrcSet(extension)}
        sizes={responsiveSizes}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        className={`optimized-image ${className}`}
        style={imgStyle}
        // @ts-ignore - fetchpriority is not in React types yet
        fetchpriority={fetchpriority}
      />
    </picture>
  );
};

export default OptimizedImage;
